// The Forecasting tab's Phone and Chat view (2026-10-24, Patrick's ask, the
// layout he approved on the design canvas): three tables on the same weeks in
// the same order, recent weeks first and then this week and the weeks ahead.
//   1. Volume    forecast, actual and variance.
//   2. Staffing plan   the forecast need on his 23% method against the schedule,
//                      with the Erlang check (speed of answer, abandon rate).
//   3. Actuals   what happened, kept apart from the plan, with realized shrinkage.
// Then a day table of where the schedule falls short of the requirement. Hours sit
// behind a toggle. No heatmap, no ranked list of hours. All the arithmetic is on
// the server (server/src/services/forecasting-phone-chat.js); this only lays it out.
import { Fragment, useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import { fetchForecastPhoneChat } from '../api.js'
import ForecastHeatmapPanel from './ForecastHeatmapPanel.jsx'
import SectionHeading from './SectionHeading.jsx'
import { BORDER, Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import {
  formatCount, formatGap, formatPercent, formatSeconds, formatVariance,
  varianceTone, gapTone, asaTone, abaTone, shrinkageTone, shortHoursTone, shortPerHourTone, EM_DASH,
} from '../forecastPhoneChat.js'

function VolumeTable({ report }) {
  const channel = (c, row) => {
    const v = row.volume[c]
    const noActual = v.actual == null
    const partial = row.isPartial && !noActual
    return (
      <>
        <Cell left>{formatCount(v.forecast)}</Cell>
        <Cell muted={noActual || partial} italic={partial}>{formatCount(v.actual)}{partial ? ' so far' : ''}</Cell>
        <Cell tone={varianceTone(v.variancePct)} muted={v.variancePct == null}>{formatVariance(v.variancePct)}</Cell>
      </>
    )
  }
  const body = (rows) => rows.map((row) => (
    <TableRow key={row.week} sx={rowSx(row)}>
      <WeekCell row={row} />
      {channel('phone', row)}
      {channel('chat', row)}
    </TableRow>
  ))
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={3} left>Phone calls</Head>
            <Head colSpan={3} left>Chats</Head>
          </TableRow>
          <TableRow>
            {['Forecast', 'Actual', 'Var'].map((h, i) => <Head key={`p${h}`} left={i === 0}>{h}</Head>)}
            {['Forecast', 'Actual', 'Var'].map((h, i) => <Head key={`c${h}`} left={i === 0}>{h}</Head>)}
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={7}>Recent weeks · what happened, oldest first</Divider>
          {body(report.recent)}
          <Divider colSpan={7}>Coming weeks · this week and the forecast</Divider>
          {body(report.coming)}
        </TableBody>
      </Table>
    </Paper>
  )
}

function PlanTable({ report, showHours }) {
  const goalSec = report.assumptions.targets.phone.asaSec
  const body = (rows) => rows.map((row) => {
    const p = row.plan
    if (!p) {
      return (
        <TableRow key={row.week} sx={rowSx(row)}>
          <WeekCell row={row} />
          <Cell left muted colSpan={12}>{EM_DASH}</Cell>
        </TableRow>
      )
    }
    const s = p.scheduled
    const e = p.erlang
    const g = p.targets
    return (
      <TableRow key={row.week} sx={rowSx(row)}>
        <WeekCell row={row} />
        <Cell left><Fte value={p.needPhone} showHours={showHours} /></Cell>
        <Cell><Fte value={p.needChat} showHours={showHours} /></Cell>
        <Cell bold><Fte value={p.needTotal} showHours={showHours} /></Cell>
        <Cell left muted={!s}><Fte value={s?.phoneTeams} showHours={showHours} /></Cell>
        <Cell muted={!s}><Fte value={s?.nickEmailChat} showHours={showHours} /></Cell>
        <Cell bold muted={!s}><Fte value={s?.total} showHours={showHours} /></Cell>
        <Cell left bold tone={gapTone(s?.gapFte)} muted={!s}>{formatGap(s?.gapFte)}</Cell>
        <Cell left tone={asaTone(e?.asaSec, goalSec)} italic={e?.projected} muted={!e}>{e ? formatSeconds(e.asaSec) : EM_DASH}</Cell>
        <Cell tone={abaTone(e?.aba)} italic={e?.projected} muted={!e}>{e ? formatPercent(e.aba) : EM_DASH}</Cell>
        <Cell left muted={!g} italic={g?.basis === 'forecast'}><Fte value={g?.phone} showHours={showHours} /></Cell>
        <Cell bold muted={!g} italic={g?.basis === 'forecast'}><Fte value={g?.total} showHours={showHours} /></Cell>
        <Cell bold tone={gapTone(g?.gapFte)} muted={g?.gapFte == null}>{formatGap(g?.gapFte)}</Cell>
      </TableRow>
    )
  })
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={3} left>FTE needed (forecast × {report.assumptions.shrinkageMarkup})</Head>
            <Head colSpan={3} left>FTE scheduled (hours ÷ {report.assumptions.scheduledHoursPerFte})</Head>
            <Head rowSpan={2} left>Gap</Head>
            <Head colSpan={2} left>Erlang check</Head>
            <Head colSpan={3} left>FTE needed to hold 2:00 and {formatPercent(report.assumptions.erlang.abaGoal)}</Head>
          </TableRow>
          <TableRow>
            {['Phone', 'Chat', 'Total'].map((h, i) => <Head key={`n${h}`} left={i === 0}>{h}</Head>)}
            {['Phone teams', 'Nick Email chat, counted', 'Total'].map((h, i) => <Head key={`s${h}`} left={i === 0}>{h}</Head>)}
            <Head left>Speed of answer</Head>
            <Head>Abandon rate</Head>
            <Head left>Phone</Head>
            <Head>Total</Head>
            <Head>Gap vs scheduled</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={13}>Recent weeks · oldest first</Divider>
          {body(report.recent)}
          <Divider colSpan={13}>
            Coming weeks · this week and the forecast
            {report.scheduledThrough ? ` (schedules run through ${report.scheduledThrough})` : ''}
          </Divider>
          {body(report.coming)}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ActualsTable({ report, showHours }) {
  const goalSec = report.assumptions.targets.phone.asaSec
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={3} left>FTE needed (actual volume)</Head>
            <Head colSpan={3} left>FTE worked (in-queue ÷ {report.assumptions.onQueueHoursPerFte})</Head>
            <Head rowSpan={2} left>Gap</Head>
            <Head rowSpan={2} left>Realized shrinkage (vs {Math.round(report.assumptions.shrinkageRate * 100)}%)</Head>
            <Head colSpan={2} left>Real</Head>
          </TableRow>
          <TableRow>
            {['Phone', 'Chat', 'Total'].map((h, i) => <Head key={`n${h}`} left={i === 0}>{h}</Head>)}
            {['Phone teams', 'Nick Email chat, counted', 'Total'].map((h, i) => <Head key={`w${h}`} left={i === 0}>{h}</Head>)}
            <Head left>Speed of answer</Head>
            <Head>Abandon rate</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.recent.map((row) => {
            const a = row.actuals
            if (!a) {
              return (
                <TableRow key={row.week}>
                  <WeekCell row={row} />
                  <Cell left muted colSpan={10}>{EM_DASH}</Cell>
                </TableRow>
              )
            }
            return (
              <TableRow key={row.week}>
                <WeekCell row={row} />
                <Cell left><Fte value={a.needPhone} showHours={showHours} /></Cell>
                <Cell><Fte value={a.needChat} showHours={showHours} /></Cell>
                <Cell bold><Fte value={a.needTotal} showHours={showHours} /></Cell>
                <Cell left><Fte value={a.workedPhoneTeams} showHours={showHours} /></Cell>
                <Cell><Fte value={a.workedNickEmailChat} showHours={showHours} /></Cell>
                <Cell bold><Fte value={a.workedTotal} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(a.gapFte)}>{formatGap(a.gapFte)}</Cell>
                <Cell left tone={shrinkageTone(a.realizedShrinkage)}>{formatPercent(a.realizedShrinkage)}</Cell>
                <Cell left tone={asaTone(a.asaSec, goalSec)} muted={a.asaSec == null}>{formatSeconds(a.asaSec)}</Cell>
                <Cell tone={abaTone(a.aba)} muted={a.aba == null}>{formatPercent(a.aba)}</Cell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}

function DayTable({ report }) {
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Day</Head>
            <Head>Open hours</Head>
            <Head>Needed, hours</Head>
            <Head>On the phones, hours</Head>
            <Head>Short, hours</Head>
            <Head>Short, agents per open hour</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.byWeekday.map((d) => (
            <TableRow key={d.weekday}>
              <TableCell sx={{ ...BORDER, py: 0.5, px: 1, fontWeight: 600 }}>{d.weekday}</TableCell>
              <Cell>{d.openHours}</Cell>
              <Cell>{d.neededHours?.toFixed(1) ?? EM_DASH}</Cell>
              <Cell>{d.onPhonesHours?.toFixed(1) ?? EM_DASH}</Cell>
              <Cell tone={shortHoursTone(d.shortHours)}>{d.shortHours?.toFixed(1) ?? EM_DASH}</Cell>
              <Cell tone={shortPerHourTone(d.shortAgentsPerOpenHour)}>{d.shortAgentsPerOpenHour?.toFixed(2) ?? EM_DASH}</Cell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastPhoneChatView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const a = report.assumptions
  const days = report.dayTableWindow
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, width: 'fit-content', maxWidth: '100%' }} title="Forecasting: Phone & Chat" descriptionMaxWidth={1100}>
        Oldest week first, running through the last complete week and on into this week and the weeks ahead. Three tables, same weeks, same order: what comes in,
        whether the schedule covers it, and what actually happened. FTE is the {Math.round(a.shrinkageRate * 100)}% standard: need is on-queue
        work ÷ {a.onQueueHoursPerFte} hours × {a.shrinkageMarkup}, with no flat spare. Scheduled is Schedule Hub's scheduled hours ÷ {a.scheduledHoursPerFte} (the
        schedule includes the unpaid half-hour lunch, so this is paid headcount). Phone work is {a.phoneAhtSec / 60} minutes a call; chat is
        {' '}{a.chatAhtSec} s over {a.chatConcurrency} chats at once, never under one agent every open hour.
        {a.callbackPerWeek != null && (
          <> Phone need also includes the 1A Spanish callback emails, work on top of the inbound calls: about {Math.round(a.callbackPerWeek)} a week at {Math.round(a.callbackAhtSec)} s
          each (Bright Pattern's raw handle time), held flat at the recent weeks' average.</>
        )}
      </SectionHeading>
      <FormControlLabel
        control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
        label="Show hours under each FTE"
        sx={{ mb: 1 }}
      />
      {report.warnings.includes('short_abandon_split_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub's short/long abandon split was missing from some rows, so every abandon is counted as long and the
          abandon rates read higher than your official rate.
        </Alert>
      )}
      {report.warnings.includes('other_unanswered_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub's other-unanswered count was missing, so the abandon rates are on the narrower answered-plus-abandoned
          basis and read higher than Power BI's.
        </Alert>
      )}
      {report.warnings.includes('patience_at_limit') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          The fitted caller patience landed at the top of the range tried, so treat the Erlang columns with care.
        </Alert>
      )}

      <Box sx={{ mt: 2, width: 'fit-content', maxWidth: '100%' }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Volume" />
        <VolumeTable report={report} />
      </Box>

      <Box sx={{ mt: 3, width: 'fit-content', maxWidth: '100%' }}>
      <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Staffing plan" descriptionMaxWidth={1100}>
        Does the schedule cover the forecast? Scheduled is the phone teams plus the part of Nick Email's schedule that is chat
        time (their share outside CXHub email blocks), counted only up to the chat need because they take chat, never phone.
        The Erlang check is the model's speed of answer and abandon rate at that staffing (Power BI's basis, short abandons left out); for past
        weeks it is the Erlang test's number, and in italics for this week and the weeks ahead it is a projection from the
        week's forecast and schedule (a day with nobody in queue, a holiday, is left out and its volume spread over the open
        days). It checks adequacy only; it adds no spare. The last three columns are the FTE that holds speed of answer at
        2:00 and abandons at 5% (the fewest agents Erlang A needs each hour, summed, plus the chat need, on the same basis as
        the plan): past weeks on that week's real calls, this week and the weeks ahead on the forecast, with no schedule
        needed. The gap is scheduled minus that need.
      </SectionHeading>
      <PlanTable report={report} showHours={showHours} />
      </Box>


      <Box sx={{ mt: 3, width: 'fit-content', maxWidth: '100%' }}>
      <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="3. Actuals" descriptionMaxWidth={1100}>
        What happened, kept apart from the plan. Need is the actual volume's on-queue work with no shrinkage added. Worked is the
        real in-queue time (phone teams, plus Nick Email's chat time up to the chat need), which already has PTO, FMLA and absence
        out of it. Realized shrinkage is the share of the phone teams' working day (the schedule less the unpaid lunch and breaks, your
        7.5-hour day) that was not in queue or on CXHub blocks, so PTO, FMLA and absence count and lunch and breaks do not,
        set against the {Math.round(a.shrinkageRate * 100)}% you plan for. Real speed of answer and abandon rate are the phone queue's, on Power BI's basis: long abandons (short ones left out)
        over every contact that reached the queue side, answered or not.
      </SectionHeading>
      <ActualsTable report={report} showHours={showHours} />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
        <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="Where the schedule falls short of the requirement" descriptionMaxWidth={760}>
        A typical day from {days.from} to {days.to}, from the Erlang test: the hours needed to hold speed of answer at
        {' '}{formatSeconds(a.erlang.asaGoalSec)} and abandons at {formatPercent(a.erlang.abaGoal)}, against what was on the phones. "Short" adds up only the hours that fell below the
        requirement; surplus in other hours does not offset it, because an agent cannot be moved between hours.
      </SectionHeading>
        <DayTable report={report} />
        </Box>
        <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
          <ForecastHeatmapPanel />
        </Box>
      </Box>

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: variance amber at ±10%, red at ±20%; gap red below 0, amber under +1; speed of answer red above 2:00; abandon rate
        amber above 5%, red above 7%; realized shrinkage amber above 23%, red above 30%. "—" means no data yet, never zero. A week
        marked event week is left out of every other week's forecast baseline.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastPhoneChat() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastPhoneChat()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastPhoneChatView report={report} />
}
